import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Clock, Star, MapPin, Car, Users, Phone, ChevronRight, CheckCircle2, X } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";
import BookingForm from "@/components/BookingForm";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const heroSlides = [
  {
    image: "/velankanni_hero.png",
    title: "Special Trichy to Velankanni Taxi",
    subtitle: "Sacred Pilgrimage Service",
    desc: "Experience a peaceful and comfortable journey to the Basilica of Our Lady of Good Health. Dedicated drivers for your spiritual pilgrimage.",
  },
  {
    image: "/trichy_rockfort_taxi_hero_1776155164304.png",
    title: "Premium Taxi Service in Trichy",
    subtitle: "#1 Rated Cab Service",
    desc: "From local sightseeing to airport drops, we ensure comfort and safety at every turn with our professional cab service.",
  },
  {
    image: "/trichy_srirangam_taxi_hero_1776155195279.png",
    title: "Temple City Tours",
    subtitle: "Explore Trichy's Heritage",
    desc: "Visit Srirangam, Rockfort, and other iconic temples with our knowledgeable local guides and comfortable rides.",
  },
];

const whyChooseUs = [
  { icon: Shield, title: "Safe & Verified Drivers", desc: "All our drivers are background-verified, trained, and experienced in navigating Trichy's roads safely." },
  { icon: Star, title: "Premium Service", desc: "Experience the highest standard of travel with our well-maintained fleet and professional chauffeurs." },
  { icon: Clock, title: "24/7 Availability", desc: "Book a cab anytime — early morning airport runs or late-night rides across Trichy, we're always available." },
  { icon: Users, title: "Group Travel", desc: "Comfortable vehicles for family trips, corporate events, and group sightseeing in Trichy." },
];

const routes = [
  { city: "Velankanni", distance: "155 km", path: "/routes/trichy-to-velankanni", price: "Starting From ₹ 4840/-", highlight: true },
  { city: "Rameswaram", distance: "225 km", path: "/routes/trichy-to-rameswaram", price: "Starting From ₹ 6800/-" },
  { city: "Madurai", distance: "140 km", path: "/routes/trichy-to-madurai", price: "Starting From ₹ 4420/-" },
  { city: "Dindigul", distance: "105 km", path: "/routes/trichy-to-dindigul", price: "Starting From ₹ 3440/-" },
  { city: "Kumbakonam", distance: "105 km", path: "/routes/trichy-to-kumbakonam", price: "Starting From ₹ 3440/-" },
  { city: "Thanjavur", distance: "65 km", path: "/routes/trichy-to-tanjore", price: "Starting From ₹ 2320/-" },
  { city: "Karaikudi", distance: "90 km", path: "/routes/trichy-to-karaikudi", price: "Starting From ₹ 3020/-" },
  { city: "Velankanni", distance: "155 km", path: "/routes/trichy-to-velankanni", price: "Starting From ₹ 4840/-" },
];

const galleryImages = [
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.41 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.51 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.52 PM (1).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.52 PM (2).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.52 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.53 PM (1).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.53 PM (2).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.53 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.54.54 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.18 PM (1).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.18 PM (2).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.18 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.19 PM (1).jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.19 PM.jpeg",
  "/gallery/WhatsApp Image 2026-04-14 at 4.59.20 PM.jpeg",
];

const popularRoutes = [
  { route: "Velankanni", dist: "155km", sedan: "4,840", suvCrysta: "7,630", suvInnova: "6,390", special: true },
  { route: "Rameswaram", dist: "225km", sedan: "6,800", suvCrysta: "10,850", suvInnova: "9,050" },
  { route: "Thanjavur", dist: "65km", sedan: "2,320", suvCrysta: "3,490", suvInnova: "2,970" },
  { route: "Pudukkottai", dist: "60km", sedan: "2,180", suvCrysta: "3,260", suvInnova: "2,780" },
  { route: "Madurai", dist: "140km", sedan: "4,420", suvCrysta: "6,940", suvInnova: "5,820" },
  { route: "Sivagangai", dist: "135km", sedan: "4,280", suvCrysta: "6,710", suvInnova: "5,630" },
  { route: "Dindigul", dist: "105km", sedan: "3,440", suvCrysta: "5,330", suvInnova: "4,490" },
  { route: "Namakkal", dist: "100km", sedan: "3,300", suvCrysta: "5,100", suvInnova: "4,300" },
  { route: "Orthanadu", dist: "80km", sedan: "2,740", suvCrysta: "4,180", suvInnova: "3,540" },
  { route: "Ramanathapuram", dist: "180km", sedan: "5,540", suvCrysta: "8,780", suvInnova: "7,340" },
  { route: "Karur", dist: "90km", sedan: "3,020", suvCrysta: "4,640", suvInnova: "3,920" },
  { route: "Karaikudi", dist: "90km", sedan: "3,020", suvCrysta: "4,640", suvInnova: "3,920" },
  { route: "Kumbakonam", dist: "105km", sedan: "3,440", suvCrysta: "5,330", suvInnova: "4,490" },
  { route: "Ariyalur", dist: "85km", sedan: "2,880", suvCrysta: "4,410", suvInnova: "3,730" },
  { route: "Thiruvarur", dist: "130km", sedan: "4,140", suvCrysta: "6,480", suvInnova: "5,440" },
  { route: "Aranthangi", dist: "80km", sedan: "2,740", suvCrysta: "4,180", suvInnova: "3,540" },
  { route: "Pattukottai", dist: "100km", sedan: "3,300", suvCrysta: "5,100", suvInnova: "4,300" },
  { route: "Mannargudi", dist: "100km", sedan: "3,300", suvCrysta: "5,100", suvInnova: "4,300" },
  { route: "Paramakudi", dist: "160km", sedan: "4,980", suvCrysta: "7,860", suvInnova: "6,580" },
  { route: "Thirumayam", dist: "60km", sedan: "2,180", suvCrysta: "3,260", suvInnova: "2,780" },
];


const cars = [
  { name: "Sedan", desc: "Swift Dzire, Etios", capacity: "4+1 Seater", features: ["AC", "Spacious Boot", "8hrs/80km", "Professional Driver"], popular: true },
  { name: "SUV", desc: "Toyota Innova", capacity: "6/7+1 Seater", features: ["AC", "Extra Luggage", "8hrs/80km", "Rugged & Safe"] },
  { name: "MUV", desc: "Innova Crysta", capacity: "7+1 Seater", features: ["High-End AC", "Great Comfort", "8hrs/80km", "Captain Seats"] },
  { name: "Tempo Traveller", desc: "Force Traveler", capacity: "14/17+1 Seater", features: ["Pushback Seats", "Large Group", "AC Luxury", "Perfect for Tours"] },
];

const testimonials = [
  { name: "Rahul Sharma", role: "Tourist from Delhi", text: "Excellent service! The driver was punctual and the car was clean. Best taxi experience in Trichy.", rating: 5 },
  { name: "Priya Nair", role: "Business Traveler", text: "I regularly use Trichy to Velankanni Taxi for my Tanjore trips. Always reliable, always on time. Highly recommended!", rating: 5 },
  { name: "Vignesh Kumar", role: "Local Resident", text: "Affordable and trustworthy. I've been using their service for airport pickups for 2 years now. Never disappointed.", rating: 5 },
  { name: "Anitha Krishnan", role: "Local Resident", text: "The best outstation taxi service in Trichy. Very professional and safe for family trips.", rating: 5 },
];

const faqs = [
  { q: "How do I book a taxi in Trichy?", a: "You can book a taxi by calling us at +91 74187 49974, messaging us on WhatsApp, or using the booking form on our website. We confirm your booking within minutes." },
  { q: "What is the fare for Trichy to Madurai taxi?", a: "We offer the best service for Trichy to Madurai outstation trips. You can choose from various car types based on your comfort and group size." },
  { q: "Do you provide airport pickup and drop service?", a: "Yes, we provide specialized airport pickup and drop services from Trichy International Airport. Our drivers track your flight for timely pickup." },
  { q: "Are your drivers verified and safe?", a: "Absolutely. All our drivers undergo thorough background verification, are professionally trained, and have years of experience driving in and around Trichy." },
  { q: "Can I book a taxi for a full day in Trichy?", a: "Yes! We offer local package rentals starting from 4 hours. Perfect for sightseeing in Trichy, visiting Srirangam, or exploring nearby attractions." },
  { q: "Do you offer outstation taxi from Trichy?", a: "Yes, we offer outstation taxi services to Madurai, Tanjore, Kumbakonam, Kodaikanal, and many more destinations from Trichy with top-quality vehicles." },
];

const Index = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Best Trichy to Velankanni Taxi Service | Affordable Cab Booking in Trichy"
        description="Book the best taxi service in Trichy for local sightseeing, Airport pickup / Airport drop, and outstation trips. Affordable fares, verified drivers, 24/7 cab service."
        keywords="trichy to velankanni taxi, taxi in trichy, cab booking trichy, trichy taxi, trichy to velankanni taxi cab service, trichy to tanjore taxi, trichy to velankanni taxi pickup, outstation taxi trichy, local sightseeing trichy cab, best taxi service trichy"
      />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TaxiService",
          "name": "Trichy to Velankanni Taxi",
          "areaServed": {
            "@type": "City",
            "name": "Trichy"
          },
          "telephone": "+91 7418 749 974",
          "url": "https://trichytovelankanni.taxi",
          "description": "Reliable taxi service in Trichy offering local rides, airport transfers, and outstation trips.",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Trichy",
            "addressRegion": "Tamil Nadu",
            "addressCountry": "India"
          }
        })}
      </script>

      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Slider */}
        <div className="absolute inset-0 z-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <img
                src={heroSlides[currentImageIndex].image}
                alt={heroSlides[currentImageIndex].title}
                className="w-full h-full object-cover"
              />
              {/* Overlays */}
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative z-10 container-custom pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.6 }}
              >
                <motion.span
                  className="inline-block px-4 py-1.5 rounded-full text-xs font-black bg-primary text-primary-foreground mb-6 uppercase tracking-widest"
                >
                  {heroSlides[currentImageIndex].subtitle}
                </motion.span>
                <motion.h1
                  className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.1] mb-8 text-white drop-shadow-2xl"
                >
                  {heroSlides[currentImageIndex].title.split(" ").map((word, idx) => (
                    word.toLowerCase() === "velankanni" ? (
                      <span key={idx} className="text-gradient block sm:inline">{word} </span>
                    ) : (
                      <span key={idx}>{word} </span>
                    )
                  ))}
                </motion.h1>
                <motion.p
                  className="text-lg sm:text-xl text-white/90 max-w-xl mb-12 leading-relaxed font-medium drop-shadow-lg"
                >
                  {heroSlides[currentImageIndex].desc}
                </motion.p>
              </motion.div>
            </AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-5"
            >
              <a href="#booking" className="btn-primary px-10 py-5 text-lg font-black shadow-2xl active:scale-95 transition-all">
                Book Your Ride
              </a>
              <a href="tel:+917418749974" className="px-10 py-5 flex items-center gap-3 text-lg font-bold text-white hover:text-primary transition-colors active:scale-95 drop-shadow-md">
                <Phone className="w-6 h-6" /> Call Now
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="lg:ml-auto w-full max-w-[350px]"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-[3rem] blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <BookingForm minimal idPrefix="hero" className="relative bg-white/10 backdrop-blur-2xl p-8 rounded-[3rem] border border-white/20 shadow-2xl" />
            </div>
          </motion.div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentImageIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-12 h-1.5 rounded-full transition-all duration-500 ${currentImageIndex === i ? "bg-primary w-20 shadow-[0_0_15px_rgba(43,138,0,0.5)]" : "bg-white/30 hover:bg-white/50"
                }`}
            />
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="container-custom">
          <SectionHeading badge="Why Choose Us" title="Trichy's Most" highlight="Trusted Taxi Service" description="We provide reliable cab services in Trichy with a focus on safety, affordability, and customer satisfaction." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, i) => (
              <motion.div
                key={item.title}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="glass-card-hover rounded-2xl p-6 text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-foreground font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES LIST */}
      <section className="py-16 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="glass-card-hover rounded-[3rem] p-8 sm:p-12 border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <SectionHeading
                  badge="Services"
                  title="Our Specialized"
                  highlight="Taxi Services"
                  description="We offer a wide range of transportation solutions tailored to your specific travel needs in and around Trichy."
                  align="left"
                />
                <div className="space-y-4 mt-8">
                  {[
                    "Airport pickup / Airport drop",
                    "Outstation trips",
                    "Local package sightseeing",
                    "Corporate cab service"
                  ].map((service, i) => (
                    <motion.div
                      key={i}
                      {...fadeUp}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-4 text-lg font-bold text-white group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary border border-primary/30 group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                        {i + 1}
                      </div>
                      {service}
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-3xl opacity-20 animate-pulse" />
                  <img
                    src="/standard_innova_suv_1774520923131.png"
                    alt="Trichy Taxi Services"
                    className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES FIXED FARES TABLE */}
      <section className="py-16 bg-taxi-darker/30">
        <div className="container-custom">
          <SectionHeading
            title="Popular Route"
            highlight="Fixed Fares"
            description="Transparent fixed pricing for our most requested outstation routes"
            align="center"
          />

          {/* Desktop Table */}
          <div className="mt-12 hidden md:block overflow-x-auto rounded-[2rem] border border-white/10 bg-background/50 backdrop-blur-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-primary/10 text-primary border-b border-white/10">
                  <th className="p-6 font-bold uppercase tracking-wider text-sm">Route</th>
                  <th className="p-6 font-bold uppercase tracking-wider text-sm">Distance</th>
                  <th className="p-6 font-bold uppercase tracking-wider text-sm">Sedan Rate</th>
                  <th className="p-6 font-bold uppercase tracking-wider text-sm">Innova Rate</th>
                  <th className="p-6 font-bold uppercase tracking-wider text-sm">Innova Crysta</th>
                </tr>
              </thead>
              <tbody>
                {popularRoutes.slice(0, 12).map((r, i) => (
                  <tr key={i} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${r.special ? "bg-primary/5" : ""}`}>
                    <td className="p-6 font-bold text-white flex items-center gap-2">
                      {r.route}
                      {r.special && (
                        <span className="text-[10px] bg-primary text-primary-foreground px-2 py-0.5 rounded-full animate-pulse">
                          POPULAR
                        </span>
                      )}
                    </td>
                    <td className="p-6 text-gray-400">{r.dist}</td>
                    <td className="p-6 text-primary font-black">₹{r.sedan}</td>
                    <td className="p-6 text-blue-400 font-black">₹{r.suvInnova}</td>
                    <td className="p-6 text-orange-400 font-black">₹{r.suvCrysta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="mt-8 space-y-4 md:hidden">
            {popularRoutes.slice(0, 8).map((r, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white mb-1">Trichy to {r.route}</h4>
                    <p className="text-sm text-gray-400">Distance: {r.dist}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-primary/10 p-2 rounded-lg text-center">
                    <p className="text-[10px] text-primary uppercase font-bold">Sedan</p>
                    <p className="text-sm font-black text-white">₹{r.sedan}</p>
                  </div>
                  <div className="bg-blue-500/10 p-2 rounded-lg text-center">
                    <p className="text-[10px] text-blue-400 uppercase font-bold">Innova</p>
                    <p className="text-sm font-black text-white">₹{r.suvInnova}</p>
                  </div>
                  <div className="bg-orange-500/10 p-2 rounded-lg text-center">
                    <p className="text-[10px] text-orange-400 uppercase font-bold">Crysta</p>
                    <p className="text-sm font-black text-white">₹{r.suvCrysta}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link to="/pricing" className="btn-primary px-8 py-3 rounded-xl text-sm font-bold">
              View All Fares
            </Link>
          </div>
          <p className="mt-6 text-center text-sm text-gray-400 font-medium font-bold">Note: Toll, Parking & permit charges extra.</p>
        </div>
      </section>

      {/* POPULAR ROUTES LINKS */}
      <section className="section-padding bg-taxi-darker/50">
        <div className="container-custom">
          <SectionHeading badge="Popular Routes" title="Outstation Taxi from" highlight="Trichy" description="Travel comfortably from Trichy to popular destinations across South India." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {routes.map((r, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <Link 
                  to={r.path} 
                  className={`glass-card-hover rounded-2xl p-6 block group relative overflow-hidden ${r.highlight ? "border-primary/50 shadow-[0_0_20px_rgba(43,138,0,0.15)]" : ""}`}
                >
                  {r.highlight && (
                    <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-bl-xl z-10">
                      BEST SELLER
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-primary" />
                    <h3 className={`font-semibold ${r.highlight ? "text-primary" : "text-foreground"}`}>Trichy → {r.city}</h3>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>Distance: {r.distance}</p>
                    <p className={r.highlight ? "text-white font-bold" : ""}>Rate: {r.price}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center text-sm text-primary font-medium group-hover:gap-2 transition-all">
                    View Details <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAR CATEGORIES */}
      <section className="section-padding">
        <div className="container-custom">
          <SectionHeading badge="Our Fleet" title="Choose Your" highlight="Perfect Ride" description="Select from our range of well-maintained vehicles for your Trichy taxi ride." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cars.map((car, i) => (
              <motion.div
                key={car.name}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className={`glass-card-hover rounded-2xl p-6 relative ${car.popular ? "border-primary/40 ring-1 ring-primary/20" : ""}`}
              >
                {car.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                    Most Popular
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                  <Car className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-foreground">{car.name}</h3>
                <p className="text-[10px] text-muted-foreground mb-1">{car.desc}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
                  <Users className="w-3 h-3" /> {car.capacity}
                </div>
                <ul className="space-y-1.5 mb-6">
                  {car.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <a href="tel:+917418749974" className="btn-primary w-full text-center block text-sm py-2">
                  Book {car.name}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY SECTION */}
      <section className="section-padding bg-background/50">
        <div className="container-custom">
          <SectionHeading
            badge="Gallery"
            title="Our Professional"
            highlight="Fleet & Service"
            description="Take a look at our well-maintained vehicles and professional service in action."
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.05 }}
                className="relative aspect-square overflow-hidden rounded-2xl group cursor-pointer"
              >
                <img
                  src={img}
                  alt={`Trichy to Velankanni Taxi Gallery ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  onClick={() => setSelectedImage(img)}
                >
                  <span className="text-white font-bold text-sm">View Large</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT PRESENCE SECTION */}
      <section className="section-padding overflow-hidden">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <SectionHeading
                badge="Terminal Presence"
                title="Our Dedicated"
                highlight="Airport Operations"
                description="We are strategically positioned at Trichy International Airport to provide you with the fastest and most reliable terminal pickup service."
                align="left"
              />
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="space-y-4">
                  <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10">
                    <h4 className="text-xl font-bold text-white mb-2">24/7 Pickup</h4>
                    <p className="text-sm text-gray-400">Our drivers are available round the clock at the arrival terminal.</p>
                  </div>
                  <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10">
                    <h4 className="text-xl font-bold text-white mb-2">Pre-Booked</h4>
                    <p className="text-sm text-gray-400">Fixed fares and guaranteed availability for all pre-booked rides.</p>
                  </div>
                </div>
                <div className="mt-8">
                  <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10 h-full flex flex-col justify-center">
                    <h4 className="text-xl font-bold text-white mb-2">Flight Tracking</h4>
                    <p className="text-sm text-gray-400">We monitor your flight status to ensure we're there exactly when you land.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <motion.div {...fadeUp} className="rounded-3xl overflow-hidden shadow-2xl h-64">
                  <img src="/ta.jpg" alt="Trichy Airport Terminal" className="w-full h-full object-cover" />
                </motion.div>
                <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="rounded-3xl overflow-hidden shadow-2xl h-48">
                  <img src="/ta2.jpg" alt="Trichy to Velankanni Taxi Service" className="w-full h-full object-cover" />
                </motion.div>
              </div>
              <div className="flex flex-col justify-center">
                <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="rounded-3xl overflow-hidden shadow-2xl h-80">
                  <img src="/ta1.jpg" alt="Trichy Airport Entrance" className="w-full h-full object-cover" />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGHTSEEING SPOTS */}
      <section className="section-padding relative overflow-hidden">
        <div className="container-custom">
          <SectionHeading
            badge="Sightseeing"
            title="Trichy Sightseeing"
            highlight="Tourist Spots"
            description="Explore the most iconic and beautiful destinations in and around Trichy."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              "Rockfort Temple (Ucchi Pillayar)",
              "Srirangam Ranganathaswamy Temple",
              "Jambukeswarar Temple, Thiruvanaikaval",
              "Samayapuram Mariamman Temple",
              "Mukkombu (Upper Anaicut)",
              "Kallanai Dam (Grand Anaicut)",
              "St. Lourdes Church",
              "Anna Science Centre Planetarium",
              "Vayalur Murugan Temple",
              "Puliyancholai Falls",
              "Pachamalai Hills"
            ].map((spot, i) => (
              <motion.div
                key={spot}
                {...fadeUp}
                transition={{ delay: i * 0.05 }}
                className="glass-card-hover p-6 rounded-2xl flex items-center gap-4 transition-shadow group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors overflow-hidden">
                  {i + 1}
                </div>
                <span className="font-semibold text-foreground">{spot}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding bg-gray-900 text-white rounded-[4rem] mx-4 overflow-hidden">
        <div className="container-custom">
          <SectionHeading badge="Testimonials" title="What Our Customers" highlight="Say About Us" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ delay: i * 0.1 }} className="bg-white/5 border border-white/10 rounded-[3rem] p-10 backdrop-blur-sm">
                <div className="flex gap-1.5 mb-6">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-gray-300 text-lg sm:text-xl italic mb-10 leading-relaxed font-light">"{t.text}"</p>
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center text-primary font-black text-xl">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{t.name}</p>
                    <p className="text-gray-500 text-sm font-medium">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="booking" className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="bg-taxi-darker border border-white/10 rounded-[3rem] shadow-2xl p-8 sm:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <SectionHeading badge="Quick Booking" title="Book Your" highlight="Trichy Taxi" description="Fill in the details below and we'll confirm your ride instantly. Safe, reliable, and affordable." />
            <div className="max-w-2xl mx-auto">
              <BookingForm idPrefix="main" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container-custom max-w-3xl">
          <SectionHeading badge="FAQ" title="Frequently Asked" highlight="Questions" />
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.details
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.05 }}
                className="bg-white/5 rounded-2xl shadow-sm hover:shadow-md transition-all border border-white/10 hover:border-primary/20 group"
              >
                <summary className="cursor-pointer p-6 text-white font-bold text-base list-none flex items-center justify-between">
                  {faq.q}
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center transition-colors group-open:bg-primary group-open:text-primary-foreground">
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </div>
                </summary>
                <p className="px-6 pb-6 text-gray-400 leading-relaxed font-medium">{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <article className="prose prose-invert prose-slate max-w-none prose-headings:text-white prose-p:text-gray-300 prose-strong:text-primary">
            <h2 className="text-3xl font-black mb-8 underline decoration-primary/30 decoration-8 underline-offset-8 text-white">Trichy to Velankanni Taxi Service – Your Ultimate Travel Partner</h2>

            <p>Looking for a reliable <strong>taxi service in Trichy</strong>? Trichy to Velankanni Taxi is your go-to cab booking service in Trichy, offering affordable, safe, and comfortable rides for local sightseeing, Airport pickup / Airport drop, and outstation travel. Whether you need a <strong>cab in Trichy City</strong>, a ride to <strong>Srirangam</strong>, or a long-distance <strong>Trichy to Madurai taxi</strong>, we have you covered with our fleet of well-maintained vehicles and professional drivers.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Why Choose Trichy to Velankanni Taxi?</h3>
            <p>Trichy, also known as Tiruchirappalli, is a historic city in Tamil Nadu renowned for its ancient temples and vibrant culture. Navigating this bustling city and its surroundings requires a dependable transportation service, and that's exactly what Trichy to Velankanni Taxi provides.</p>

            <p>Our fleet includes a range of vehicles from comfortable sedans like Maruti Dzire and Toyota Etios for families and business trips, to spacious SUVs like Toyota Innova and Maruti Ertiga for group travel. For larger groups, we offer 14-seater Tempo Travellers, ensuring the perfect ride for every group size.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Local & Outstation Services</h3>
            <p>Explore the best of Trichy with our local <strong>taxi service in Trichy</strong>. Visit the iconic Rockfort Temple, the serene Srirangam Temple, the Jambukeswarar Temple, and many more. Our outstation packages connect you to major cities across South India including Madurai, Tanjore, Kumbakonam, and Kodaikanal at the best market rates.</p>

            <h3 className="text-2xl font-bold mt-12 mb-6 text-white">Trichy Airport Transfers</h3>
            <p>Arriving at Trichy International Airport? Our <strong>Trichy to Velankanni taxi</strong> service offers competitive rates. We track your flight in real-time, ensuring your driver is waiting for you at the arrivals terminal, providing a hassle-free transition to your destination.</p>

            <p>Ready to explore? Book your Trichy taxi today with professional service and local expertise. Your journey in Trichy starts with us!</p>
          </article>
        </div>
      </section>
      {/* IMAGE MODAL */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full aspect-video"
            >
              <img
                src={selectedImage}
                alt="Enlarged gallery item"
                className="w-full h-full object-contain rounded-2xl"
              />
              <button
                className="absolute -top-12 right-0 text-white hover:text-primary transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
              >
                <X className="w-8 h-8" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Index;
